import type {
  ThreadResponse,
  TurnResponse,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {produce} from 'immer';

import {
  parseThreadTitle,
  parseTurnTitle,
} from '../components/WBAgent/threadTitle';
import {
  agentQuestionsEventForTurn,
  networkAccessRequestForTurn,
  networkPermissionsEventForTurn,
  terminalEventForTurnState,
  TurnEventExtractor,
  userMessageEventForTurn,
} from './runtime/turn';
import {applyRuntimeEventToThread} from './store/eventReducer';
import type {Thread} from './store/thread';
import {createEmptyThread} from './store/thread';

/**
 * buildThreadFromTurns reconstructs a `Thread` from a chronologically-sorted
 * array of `TurnResponse`s. All turns are replayed through the event pipeline
 * (`TurnEventExtractor` + `applyRuntimeEventToThread`) in a single Immer
 * produce call, producing an identical `MessageTree` to what live streaming
 * would create.
 */
export function buildThreadFromTurns(
  threadId: string,
  turns: TurnResponse[],
  threadMetadata?: ThreadResponse
): Thread {
  return produce(createEmptyThread(threadId), draft => {
    for (const turn of turns) {
      const extractor = new TurnEventExtractor(turn.id);
      const events = extractor.extract(turn);
      const now = new Date(turn.created_at).getTime();
      draft.latestTurnId = turn.id;

      applyRuntimeEventToThread(
        draft,
        {type: 'run-started', runId: turn.id, threadId},
        now
      );

      // A newly queued/in-progress turn can expose user_prompt before its user
      // message row reaches history. Reconstruct that canonical prompt before
      // assistant events so a background-hydrated thread has the same shape as
      // a later reload. Preserve the existing errored-turn banner-only fallback.
      if (
        turn.state !== 'errored' &&
        !events.some(event => event.type === 'user-message')
      ) {
        const userEvent = userMessageEventForTurn(turn);
        if (userEvent) {
          applyRuntimeEventToThread(draft, userEvent, now);
        }
      }

      for (const event of events) {
        applyRuntimeEventToThread(draft, event, now);
      }

      // The active network allowlist is the turn's resolved permissions, known
      // for every turn regardless of state — so reflect it even when the last
      // turn is still in progress (don't gate it on the terminal event).
      applyRuntimeEventToThread(
        draft,
        networkPermissionsEventForTurn(turn),
        now
      );

      const terminalEvent = terminalEventForTurnState(turn.state, turn.id);
      if (terminalEvent) {
        // Each turn's `run-started` clears the prior turn's pendingQuestions,
        // so replaying these leaves only the last turn's questions standing.
        const questionsEvent = agentQuestionsEventForTurn(turn);
        if (questionsEvent) {
          applyRuntimeEventToThread(draft, questionsEvent, now);
        }
        const networkRequestEvent = networkAccessRequestForTurn(turn);
        if (networkRequestEvent) {
          applyRuntimeEventToThread(draft, networkRequestEvent, now);
        }
        applyRuntimeEventToThread(draft, terminalEvent, now);
      }
    }

    // Replay uses `currentRunId` as scratch, since `settleRun` only finalizes
    // the turn matching it. Clear it so a still-running last turn leaves no
    // backend turn id in a field that otherwise holds client-minted run ids.
    draft.currentRunId = null;

    if (turns.length > 0) {
      const first = turns[0];
      const last = turns[turns.length - 1];

      if (threadMetadata) {
        const {title, promptSnippet} = parseThreadTitle(threadMetadata);
        draft.title = title;
        draft.promptSnippet = promptSnippet;
        draft.createdAt = new Date(threadMetadata.created_at).getTime();
        draft.updatedAt = new Date(threadMetadata.updated_at).getTime();
        draft.lastActivityAt = new Date(
          threadMetadata.last_turn_created_at
        ).getTime();
        draft.projects = extractProjectsForThread(threadMetadata);
      } else {
        const {title, promptSnippet} = parseTurnTitle(first);
        draft.title = title;
        draft.promptSnippet = promptSnippet;
        draft.createdAt = new Date(first.created_at).getTime();
        draft.updatedAt = new Date(first.updated_at).getTime();
        draft.lastActivityAt = new Date(last.created_at).getTime();
        draft.projects = extractProjectsForRootTurn(first);
      }
    }
  });
}

type ThreadProject = {entityName: string; projectName: string};

function projectsFromWandbScope(
  entity: string | undefined,
  project: string | undefined
): ThreadProject[] {
  if (!entity || !project) {
    return [];
  }

  return [{entityName: entity, projectName: project}];
}

/**
 * extractProjectsForThread returns the projects relevant to a thread record
 * from GET /threads.
 */
export function extractProjectsForThread(
  thread: ThreadResponse
): ThreadProject[] {
  return projectsFromWandbScope(thread.wandb_entity, thread.wandb_project);
}

/**
 * extractProjectsForRootTurn returns the projects carried on a root turn
 * when hydrating a thread from GET /turns.
 *
 * NOTE: The legacy turn-level entity/project fields are deprecated. Once
 * turns drop them, this should parse user-prompt parts instead.
 */
export function extractProjectsForRootTurn(
  turn: TurnResponse
): ThreadProject[] {
  return projectsFromWandbScope(turn.wandb_entity, turn.wandb_project);
}
